import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useForm } from "react-hook-form";
import { generateEmailHTML } from "../utils/emailTemplateGenerator";
import { generateConfirmationEmailHTML } from "../utils/confirmationEmailGenerator";
import generateDepositReceivedEmailHTML from "../utils/depositReceivedEmailGenerator";
import generateWhyChooseUsEmailHTML from "../utils/whyChooseUsEmailGenerator";
import generateJobCompleteEmailHTML from "../utils/jobCompleteEmailGenerator";
import generateAffiliateMarketingEmailHTML from "../utils/affiliateMarketingEmailGenerator";
import generatePartnershipEmailHTML from "../utils/partnershipEmailGenerator";
import generateCustomMessageEmailHTML from "../utils/customMessageEmailGenerator";
import generateInvoiceHTML from "../utils/invoiceGenerator";
import generatePropertyViewingEmailHTML, { DEFAULT_VIEWING_MESSAGE, DEFAULT_CALLBACK_MESSAGE } from "../utils/propertyViewingEmailGenerator";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { useAuth } from "../context/AuthContext";
import { DirectorMessage } from "../components/DirectorMessage";
import {
  Tablet,
  Smartphone,
  Copy,
  Download,
  RefreshCw,
  CheckCircle2,
  Truck,
  X,
  User,
  FileText,
  CheckCircle,
  Send,
  Mail,
  Link2,
  Loader2,
  Eye,
  Edit3,
  Paperclip,
  Users,
  Receipt,
  AlertTriangle,
  Star,
  Award,
  Save,
  Plus,
  Smartphone as InstallIcon,
  Search,
  Filter,
  Calendar,
  CalendarClock,
  Trash2,
  Play,
  BarChart3,
  FileDown,
  MessageSquare,
  Gift,
  Building,
  Home,
  Monitor,
} from "lucide-react";
import axios from "axios";
import { SUNNY_IMAGE_URL, PROPERTY_SIZES, CREW_SIZES, VEHICLE_TYPES, CUSTOMER_STATUSES, PACKAGE_OPTIONS, DEFAULT_FORM_VALUES } from "../components/EmailBuilder/constants";
import { MessageForm } from "../components/EmailBuilder/MessageForm";
import { QuotationForm } from "../components/EmailBuilder/QuotationForm";
import { ConfirmationForm } from "../components/EmailBuilder/ConfirmationForm";
import { DepositForm } from "../components/EmailBuilder/DepositForm";
import { ReviewForm } from "../components/EmailBuilder/ReviewForm";
import { AffiliateForm } from "../components/EmailBuilder/AffiliateForm";
import { WhyChooseForm } from "../components/EmailBuilder/WhyChooseForm";
import { ViewingForm } from "../components/EmailBuilder/ViewingForm";
import { PartnershipForm } from "../components/EmailBuilder/PartnershipForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


const SENDER_BRANCH_OPTIONS = [
  { value: "head_office", label: "Head Office" },
  { value: "berkshire", label: "Berkshire" },
  { value: "buckinghamshire", label: "Buckinghamshire" },
  { value: "hampshire", label: "Hampshire" },
  { value: "oxfordshire", label: "Oxfordshire" },
  { value: "surrey", label: "Surrey" },
  { value: "london", label: "London" },
];

export default function EmailBuilder() {
  const { user: authUser, logout } = useAuth();
  const [emailType, setEmailType] = useState("quotation");
  const [viewMode, setViewMode] = useState("desktop");
  const [mobileView, setMobileView] = useState("edit"); // 'edit' or 'preview'
  const [htmlOutput, setHtmlOutput] = useState("");
  const [copied, setCopied] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [senderAccounts, setSenderAccounts] = useState([]);
  const [newSenderEmail, setNewSenderEmail] = useState("");
  const [newSenderBranch, setNewSenderBranch] = useState("head_office");
  const [showSenderManager, setShowSenderManager] = useState(false);
  const [sending, setSending] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [customerEmail, setCustomerEmail] = useState("");
  const [senderEmail, setSenderEmail] = useState("office@homego-removals.com");
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [attachment, setAttachment] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [crewList, setCrewList] = useState([]);
  const [selectedCrewIds, setSelectedCrewIds] = useState([]);
  const [newCrewName, setNewCrewName] = useState("");
  const [newCrewRole, setNewCrewRole] = useState("Mover");
  // PWA Install State
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  // Customer Search/Filter State
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerStatusFilter, setCustomerStatusFilter] = useState("all");
  // Email Scheduling State
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("09:00");
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [scheduling, setScheduling] = useState(false);
  // Email Tracking State
  const [sentEmails, setSentEmails] = useState([]);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  // Referral Tracking State
  const [referrals, setReferrals] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showAddReferralModal, setShowAddReferralModal] = useState(false);
  const [newReferral, setNewReferral] = useState({ referred_name: "", referred_email: "", referred_phone: "" });
  const [savingReferral, setSavingReferral] = useState(false);
  const fileInputRef = useRef(null);
  const iframeRef = useRef(null);

  // Fetch customers on load and when search/filter changes
  useEffect(() => {
    fetchCustomers();
  }, [customerSearch, customerStatusFilter]);

  // Fetch scheduled emails on load
  useEffect(() => {
    fetchScheduledEmails();
    fetchSentEmails();
  }, []);

  const fetchCustomers = async () => {
    try {
      const params = new URLSearchParams();
      if (customerSearch) params.append('search', customerSearch);
      if (customerStatusFilter && customerStatusFilter !== 'all') params.append('status', customerStatusFilter);
      
      const response = await axios.get(`${API}/customers?${params.toString()}`);
      setCustomers(response.data);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  const fetchCrew = async () => {
    try {
      const response = await axios.get(`${API}/crew`);
      setCrewList(response.data.crew || []);
    } catch (error) {
      console.error("Failed to fetch crew:", error);
    }
  };

  useEffect(() => { fetchCrew(); }, []);

  const addCrewMember = async () => {
    if (!newCrewName.trim()) { toast.error("Enter crew member name"); return; }
    try {
      const { data } = await axios.post(`${API}/crew`, { name: newCrewName.trim(), role: newCrewRole });
      setCrewList((prev) => [...prev, data]);
      setSelectedCrewIds((prev) => [...prev, data.id]);
      setNewCrewName("");
      toast.success(`${data.name} added to crew`);
    } catch (error) {
      toast.error("Failed to add crew member");
    }
  };

  const removeCrewMember = async (crewId) => {
    try {
      await axios.delete(`${API}/crew/${crewId}`);
      setCrewList((prev) => prev.filter((m) => m.id !== crewId));
      setSelectedCrewIds((prev) => prev.filter((id) => id !== crewId));
      toast.success("Crew member removed");
    } catch (error) {
      toast.error("Failed to remove crew member");
    }
  };

  const saveViewingDraft = () => {
    const draft = {
      customer_name: formValues.customer_name,
      customerEmail,
      viewing_property_address: formValues.viewing_property_address,
      viewing_date: formValues.viewing_date,
      viewing_time: formValues.viewing_time,
      viewing_customer_mobile: formValues.viewing_customer_mobile,
      viewing_magic_words: formValues.viewing_magic_words,
      selectedCrewIds,
    };
    localStorage.setItem("hg_viewing_draft", JSON.stringify(draft));
    toast.success("Viewing draft saved");
  };

  const resetViewingForm = () => {
    setValue("viewing_property_address", "");
    setValue("viewing_date", "");
    setValue("viewing_time", "");
    setValue("viewing_customer_mobile", "");
    setValue("viewing_magic_words", DEFAULT_VIEWING_MESSAGE);
    setSelectedCrewIds([]);
    toast.success("Viewing form reset");
  };

  const fetchScheduledEmails = async () => {
    try {
      const response = await axios.get(`${API}/scheduled-emails?status=pending`);
      setScheduledEmails(response.data);
    } catch (error) {
      console.error("Failed to fetch scheduled emails:", error);
    }
  };

  const fetchSentEmails = async () => {
    try {
      const response = await axios.get(`${API}/sent-emails?limit=20`);
      setSentEmails(response.data);
    } catch (error) {
      console.error("Failed to fetch sent emails:", error);
    }
  };

  // Fetch referrals and stats
  const fetchReferrals = async () => {
    try {
      const [referralsRes, statsRes] = await Promise.all([
        axios.get(`${API}/referrals`),
        axios.get(`${API}/referrals/stats/summary`)
      ]);
      setReferrals(referralsRes.data);
      setReferralStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch referrals:", error);
    }
  };

  // Fetch referrals on load
  useEffect(() => {
    fetchReferrals();
  }, []);

  // Add a new referral
  const handleAddReferral = async () => {
    if (!selectedCustomer) {
      toast.error("Please select a customer first to add a referral");
      return;
    }
    if (!newReferral.referred_name) {
      toast.error("Please enter the referred person's name");
      return;
    }

    setSavingReferral(true);
    try {
      const referralCode = formValues.affiliate_referral_code || 
        `HOMEGO-${formValues.customer_name.split(' ')[0].toUpperCase().slice(0,4)}${Math.floor(Math.random() * 1000)}`;
      
      await axios.post(`${API}/referrals`, {
        referrer_id: selectedCustomer.id,
        referrer_name: selectedCustomer.name,
        referrer_email: selectedCustomer.email,
        referrer_code: referralCode,
        referred_name: newReferral.referred_name,
        referred_email: newReferral.referred_email,
        referred_phone: newReferral.referred_phone,
        reward_amount: parseFloat(formValues.affiliate_referral_amount) || 100
      });
      
      toast.success(`Referral added! ${selectedCustomer.name} referred ${newReferral.referred_name}`);
      setNewReferral({ referred_name: "", referred_email: "", referred_phone: "" });
      setShowAddReferralModal(false);
      fetchReferrals();
    } catch (error) {
      toast.error("Failed to add referral");
    }
    setSavingReferral(false);
  };

  // Convert a referral
  const handleConvertReferral = async (referralId, bookingRef = "") => {
    try {
      await axios.patch(`${API}/referrals/${referralId}/convert?booking_reference=${bookingRef}`);
      toast.success("Referral marked as converted! £100 reward is now owed.");
      fetchReferrals();
    } catch (error) {
      toast.error("Failed to convert referral");
    }
  };

  // Mark referral as paid
  const handlePayReferral = async (referralId) => {
    try {
      await axios.patch(`${API}/referrals/${referralId}/pay`);
      toast.success("Reward marked as paid!");
      fetchReferrals();
    } catch (error) {
      toast.error("Failed to mark as paid");
    }
  };

  // Delete a referral
  const handleDeleteReferral = async (referralId) => {
    if (!window.confirm("Are you sure you want to delete this referral?")) return;
    try {
      await axios.delete(`${API}/referrals/${referralId}`);
      toast.success("Referral deleted");
      fetchReferrals();
    } catch (error) {
      toast.error("Failed to delete referral");
    }
  };

  // PWA Install Prompt Handler
  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsAppInstalled(true);
      return;
    }
    
    // Check if banner was dismissed this session
    if (sessionStorage.getItem('installBannerDismissed')) {
      return;
    }
    
    // iOS Safari detection - show manual install instructions
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    
    // Listen for beforeinstallprompt (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallBanner(true);
    };
    
    // Listen for successful installation
    const handleAppInstalled = () => {
      setIsAppInstalled(true);
      setInstallPrompt(null);
      setShowInstallBanner(false);
      toast.success("App installed successfully!");
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    // Show banner for iOS users after a delay
    if (isIOS && isSafari && !isAppInstalled) {
      setTimeout(() => setShowInstallBanner(true), 3000);
    }
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isAppInstalled]);

  const saveCustomer = async () => {
    setSavingCustomer(true);
    try {
      const customerData = {
        name: formValues.customer_name,
        email: customerEmail,
        phone: formValues.phone_number || "",
        property_size: formValues.property_size,
        moving_from: formValues.moving_from,
        moving_to: formValues.moving_to,
        moving_date: formValues.moving_date,
        selected_package: formValues.selected_package,
        total_amount: formValues.service_total?.toString() || "",
        deposit_amount: formValues.deposit_amount?.toString() || "",
        notes: formValues.additional_notes,
        status: "pending"
      };

      if (selectedCustomer) {
        await axios.put(`${API}/customers/${selectedCustomer.id}`, customerData);
        toast.success("Customer updated!");
      } else {
        const response = await axios.post(`${API}/customers`, customerData);
        setValue("quotation_reference", response.data.reference);
        toast.success(`Customer saved! Ref: ${response.data.reference}`);
        
        // Track Google Ads conversion for new customer/lead
        if (window.trackGoogleAdsConversion) {
          window.trackGoogleAdsConversion();
        }
      }
      
      fetchCustomers();
      setShowCustomerModal(false);
    } catch (error) {
      toast.error("Failed to save customer");
    }
    setSavingCustomer(false);
  };

  const loadCustomer = (customer) => {
    setSelectedCustomer(customer);
    setValue("quotation_reference", customer.reference);
    setValue("customer_name", customer.name);
    if (customer.email) setCustomerEmail(customer.email);
    if (customer.phone) setValue("phone_number", customer.phone);
    if (customer.property_size) setValue("property_size", customer.property_size);
    if (customer.moving_from) setValue("moving_from", customer.moving_from);
    if (customer.moving_to) setValue("moving_to", customer.moving_to);
    if (customer.moving_date) setValue("moving_date", customer.moving_date);
    if (customer.selected_package) setValue("selected_package", customer.selected_package);
    if (customer.total_amount) setValue("service_total", parseFloat(customer.total_amount));
    if (customer.deposit_amount) setValue("deposit_amount", parseFloat(customer.deposit_amount));
    if (customer.notes) setValue("additional_notes", customer.notes);
    // Auto-fill Deposit tab fields
    if (customer.moving_from) setValue("deposit_moving_from_full", customer.moving_from);
    if (customer.moving_to) setValue("deposit_moving_to_full", customer.moving_to);
    if (customer.total_amount) setValue("deposit_total_amount", String(customer.total_amount));
    if (customer.deposit_amount) setValue("deposit_received_amount", String(customer.deposit_amount));
    if (customer.selected_package) {
      const pkgMap = {
        "Loading Crew Only": "loading_crew",
        "Man & Van": "man_and_van",
        "Economy Move": "economy",
        "Comfort Move": "comfort",
        "Comfort Plus": "comfort_plus",
        "Premium - Pack, Move & Unpack": "premium",
      };
      if (pkgMap[customer.selected_package]) {
        setValue("deposit_selected_package", pkgMap[customer.selected_package]);
      }
    }
    // Auto-fill Confirm tab fields
    if (customer.total_amount) setValue("invoice_move_price", parseFloat(customer.total_amount));
    if (customer.deposit_amount) setValue("invoice_deposit_amount", parseFloat(customer.deposit_amount));
    toast.success(`Loaded: ${customer.name} (${customer.reference}) — Quote, Confirm & Deposit tabs auto-filled`);
  };

  const updateCustomerStatus = async (customerId, status, scheduledCall = null) => {
    try {
      await axios.patch(`${API}/customers/${customerId}/status?status=${status}${scheduledCall ? `&scheduled_call=${scheduledCall}` : ''}`);
      fetchCustomers();
      toast.success("Status updated!");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deleteSelectedCustomer = async () => {
    if (!selectedCustomer) return;
    if (!window.confirm(`Delete customer "${selectedCustomer.name}" (${selectedCustomer.reference})? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/customers/${selectedCustomer.id}`);
      toast.success(`Deleted ${selectedCustomer.name}`);
      setSelectedCustomer(null);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete customer");
    }
  };

  const { register, watch, setValue, reset } = useForm({
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const formValues = watch();
  
  // Debounced form values to prevent lag
  const [debouncedValues, setDebouncedValues] = useState(formValues);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValues(formValues);
    }, 300); // 300ms debounce
    return () => clearTimeout(timer);
  }, [formValues]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate device type based on windowWidth
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [refResponse, statusResponse, sendersResponse] = await Promise.all([
          axios.get(`${API}/next-reference`),
          axios.get(`${API}/oauth/gmail/status`),
          axios.get(`${API}/sender-accounts`)
        ]);
        setValue("quotation_reference", refResponse.data.reference);
        setConnectedAccounts(statusResponse.data.connected_accounts || []);
        const accounts = sendersResponse.data.accounts || [];
        setSenderAccounts(accounts);
        if (accounts.length > 0 && !accounts.some(a => a.email === "office@homego-removals.com")) {
          setSenderEmail(accounts[0].email);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };
    fetchInitialData();
  }, [setValue]);

  // Listen for OAuth callback messages
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'gmail_connected') {
        toast.success(`Connected: ${event.data.email}`);
        axios.get(`${API}/oauth/gmail/status`).then(res => {
          setConnectedAccounts(res.data.connected_accounts || []);
        });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Update HTML whenever form values change (debounced)
  useEffect(() => {
    let html;
    if (emailType === "quotation") {
      html = generateEmailHTML(debouncedValues);
    } else if (emailType === "confirmation") {
      html = generateConfirmationEmailHTML(debouncedValues);
    } else if (emailType === "deposit") {
      html = generateDepositReceivedEmailHTML({
        customer_name: debouncedValues.customer_name,
        quotation_reference: debouncedValues.quotation_reference,
        move_date: debouncedValues.moving_date,
        arrival_time: debouncedValues.arrival_time,
        moving_from: debouncedValues.deposit_moving_from_full,
        moving_to: debouncedValues.deposit_moving_to_full,
        selected_package: debouncedValues.deposit_selected_package,
        total_amount: debouncedValues.deposit_total_amount,
        deposit_received: debouncedValues.deposit_received_amount,
        van_size: debouncedValues.vehicle_type || debouncedValues.deposit_van_size,
        crew_count: debouncedValues.crew_size?.replace(" Crew Members", "").replace(" Crew Member", "") || debouncedValues.deposit_crew_count,
        whats_next: debouncedValues.deposit_whats_next,
        our_commitment: debouncedValues.deposit_our_commitment,
        important_notes: debouncedValues.deposit_important_notes,
        bank_account_name: debouncedValues.bank_account_name,
        bank_sort_code: debouncedValues.bank_sort_code,
        bank_account_number: debouncedValues.bank_account_number,
        payment_link: debouncedValues.deposit_payment_link,
        // Crew Recommendation
        booked_crew_count: debouncedValues.booked_crew_count,
        booked_van_type: debouncedValues.booked_van_type,
        recommended_crew_count: debouncedValues.recommended_crew_count,
        recommended_van_type: debouncedValues.recommended_van_type,
        show_crew_recommendation: debouncedValues.show_crew_recommendation,
        // Working Hours & Overtime Policy
        show_overtime_policy: debouncedValues.show_overtime_policy,
        overtime_rate: debouncedValues.overtime_rate,
        work_hours_start: debouncedValues.work_hours_start,
        work_hours_end: debouncedValues.work_hours_end,
        // Custom Message
        custom_recommendation_message: debouncedValues.custom_recommendation_message,
        show_custom_message: debouncedValues.show_custom_message,
        // Multiple Locations
        pickup_location_1: debouncedValues.pickup_location_1,
        pickup_location_2: debouncedValues.pickup_location_2,
        dropoff_location_1: debouncedValues.dropoff_location_1,
        dropoff_location_2: debouncedValues.dropoff_location_2,
        stop_location_1: debouncedValues.stop_location_1,
        stop_location_2: debouncedValues.stop_location_2,
        show_extra_pickups: debouncedValues.show_extra_pickups,
        show_extra_dropoffs: debouncedValues.show_extra_dropoffs,
        show_stops: debouncedValues.show_stops,
        // Special Requirements
        special_requirements: debouncedValues.special_requirements,
        show_special_requirements: debouncedValues.show_special_requirements,
      });
    } else if (emailType === "whychoose") {
      html = generateWhyChooseUsEmailHTML({
        customer_name: debouncedValues.customer_name,
        quotation_reference: debouncedValues.quotation_reference,
        headline: debouncedValues.why_headline,
        intro_message: debouncedValues.why_intro,
        our_promises: debouncedValues.why_promises,
        // Consultant & Company
        consultant_name: debouncedValues.why_consultant_name,
        consultant_title: debouncedValues.why_consultant_title,
        company_reg_number: debouncedValues.why_company_reg,
        companies_house_link: debouncedValues.why_companies_house_link,
        insurance_amount: debouncedValues.why_insurance_amount,
        video_upload_link: debouncedValues.why_video_upload_link,
        // Van Images
        luton_van_image: debouncedValues.why_luton_van_image,
        maxi_mover_image: debouncedValues.why_maxi_mover_image,
        ton_lorry_image: debouncedValues.why_ton_lorry_image,
        // Service Packages
        economy_move_title: debouncedValues.why_economy_title,
        economy_move_details: debouncedValues.why_economy_details,
        economy_move_ideal: debouncedValues.why_economy_ideal,
        comfort_move_title: debouncedValues.why_comfort_title,
        comfort_move_details: debouncedValues.why_comfort_details,
        comfort_move_ideal: debouncedValues.why_comfort_ideal,
        complete_comfort_title: debouncedValues.why_complete_title,
        complete_comfort_details: debouncedValues.why_complete_details,
        complete_comfort_ideal: debouncedValues.why_complete_ideal,
        premium_move_title: debouncedValues.why_premium_title,
        premium_move_details: debouncedValues.why_premium_details,
        premium_move_ideal: debouncedValues.why_premium_ideal,
        // Reviews
        review_1: debouncedValues.why_review_1,
        review_1_author: debouncedValues.why_review_1_author,
        review_1_location: debouncedValues.why_review_1_location,
        review_1_package: debouncedValues.why_review_1_package,
        review_2: debouncedValues.why_review_2,
        review_2_author: debouncedValues.why_review_2_author,
        review_2_location: debouncedValues.why_review_2_location,
        review_2_package: debouncedValues.why_review_2_package,
        review_3: debouncedValues.why_review_3,
        review_3_author: debouncedValues.why_review_3_author,
        review_3_location: debouncedValues.why_review_3_location,
        review_3_package: debouncedValues.why_review_3_package,
        review_4: debouncedValues.why_review_4,
        review_4_author: debouncedValues.why_review_4_author,
        review_4_location: debouncedValues.why_review_4_location,
        review_4_package: debouncedValues.why_review_4_package,
      });
    } else if (emailType === "review") {
      html = generateJobCompleteEmailHTML({
        customer_name: debouncedValues.customer_name,
        moving_from: debouncedValues.moving_from,
        moving_to: debouncedValues.moving_to,
        move_date: debouncedValues.moving_date,
        quotation_reference: debouncedValues.quotation_reference,
        google_review_link: debouncedValues.review_google_link,
        trustpilot_link: debouncedValues.review_trustpilot_link,
        custom_message: debouncedValues.review_custom_message,
        consultant_name: debouncedValues.consultant_name,
        consultant_title: debouncedValues.consultant_title,
      });
    } else if (emailType === "affiliate") {
      html = generateAffiliateMarketingEmailHTML({
        customer_name: debouncedValues.customer_name,
        referral_amount: debouncedValues.affiliate_referral_amount,
        referral_code: debouncedValues.affiliate_referral_code || `HOMEGO-${debouncedValues.customer_name.split(' ')[0].toUpperCase().slice(0,4)}${Math.floor(Math.random() * 1000)}`,
        headline: debouncedValues.affiliate_headline,
        intro_message: debouncedValues.affiliate_intro,
        how_it_works_1: debouncedValues.affiliate_how_it_works_1,
        how_it_works_2: debouncedValues.affiliate_how_it_works_2,
        how_it_works_3: debouncedValues.affiliate_how_it_works_3,
        share_message: debouncedValues.affiliate_share_message,
        terms_conditions: debouncedValues.affiliate_terms,
        consultant_name: debouncedValues.consultant_name,
        consultant_title: debouncedValues.consultant_title,
      });
    } else if (emailType === "partnership") {
      html = generatePartnershipEmailHTML({
        partner_name: debouncedValues.partner_name,
        partner_company: debouncedValues.partner_company,
        partner_type: debouncedValues.partner_type,
        area_name: debouncedValues.partner_area,
        director_name: debouncedValues.partner_director_name,
        company_name: debouncedValues.partner_company_name,
        company_reg: debouncedValues.company_reg_number,
        company_phone: debouncedValues.phone_number,
        company_email: debouncedValues.partner_company_email,
        company_website: debouncedValues.partner_company_website,
        commission_amount: debouncedValues.partner_commission,
        services: debouncedValues.partner_services,
        insurance_amount: debouncedValues.partner_insurance,
        intro_message: debouncedValues.partner_intro_message,
        closing_message: debouncedValues.partner_closing_message,
        show_attachments: debouncedValues.partner_show_attachments,
        benefit_1: debouncedValues.partner_benefit_1,
        benefit_2: debouncedValues.partner_benefit_2,
        benefit_3: debouncedValues.partner_benefit_3,
        benefit_4: debouncedValues.partner_benefit_4,
        benefit_5: debouncedValues.partner_benefit_5,
      });
    } else if (emailType === "message") {
      html = generateCustomMessageEmailHTML({
        customer_name: debouncedValues.message_customer_name || debouncedValues.customer_name,
        subject_line: debouncedValues.message_subject,
        message_content: debouncedValues.message_content,
        include_contact_footer: debouncedValues.message_include_contact !== false,
        consultant_name: debouncedValues.consultant_name || "Thomas Jacobs",
        consultant_title: debouncedValues.consultant_title || "Moving Consultant",
        consultant_phone: debouncedValues.phone_number || "0330 043 4791",
        consultant_email: debouncedValues.consultant_email || "info@homego-removals.com"
      });
    } else if (emailType === "viewing") {
      html = generatePropertyViewingEmailHTML({
        customer_name: debouncedValues.customer_name,
        property_address: debouncedValues.viewing_property_address,
        viewing_date: debouncedValues.viewing_date,
        viewing_time: debouncedValues.viewing_time,
        customer_mobile: debouncedValues.viewing_customer_mobile,
        appointment_type: debouncedValues.viewing_type || "viewing",
        trust_message: debouncedValues.viewing_magic_words || (debouncedValues.viewing_type === "callback" ? DEFAULT_CALLBACK_MESSAGE : DEFAULT_VIEWING_MESSAGE),
        crew: crewList.filter((m) => selectedCrewIds.includes(m.id)),
        verify_base_url: process.env.REACT_APP_BACKEND_URL,
      });
    }
    setHtmlOutput(html);
  }, [debouncedValues, emailType, crewList, selectedCrewIds]);

  // Update iframe content
  useEffect(() => {
    if (iframeRef.current && htmlOutput) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(htmlOutput);
        doc.close();
      }
    }
  }, [htmlOutput, mobileView]);

  // Update package description
  useEffect(() => {
    const pkg = PACKAGE_OPTIONS.find(p => p.value === debouncedValues.selected_package);
    if (pkg) setValue("package_description", pkg.description);
  }, [debouncedValues.selected_package, setValue]);

  const handleCopyHTML = async () => {
    try {
      await navigator.clipboard.writeText(htmlOutput);
      setCopied(true);
      toast.success("HTML copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const handleDownloadHTML = () => {
    const blob = new Blob([htmlOutput], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const typeNames = {
      quotation: "Quote",
      confirmation: "Confirmation",
      deposit: "Deposit-Received",
      review: "Review-Request",
      affiliate: "Referral-Program",
      whychoose: "Why-Choose-Us"
    };
    a.download = `HomeGo-${typeNames[emailType] || "Email"}-${formValues.quotation_reference}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Downloaded!");
  };

  // Generate Invoice Data
  const getInvoiceData = () => ({
    customer_name: formValues.customer_name,
    customer_email: formValues.invoice_customer_email || customerEmail,
    customer_phone: formValues.invoice_customer_phone || formValues.phone_number || "",
    quotation_reference: formValues.quotation_reference,
    moving_from: formValues.moving_from,
    moving_to: formValues.moving_to,
    move_date: formValues.moving_date,
    service_name: formValues.invoice_service_name || formValues.selected_package,
    service_description: formValues.invoice_service_description || formValues.package_description,
    total_amount: formValues.invoice_move_price || formValues.service_total || formValues.comfort_move_price,
    deposit_amount: formValues.invoice_deposit_amount || formValues.deposit_amount || "100",
    van_size: formValues.deposit_van_size || formValues.vehicle_type,
    crew_count: formValues.deposit_crew_count || formValues.crew_size,
    bank_account_name: formValues.bank_account_name,
    bank_sort_code: formValues.bank_sort_code,
    bank_account_number: formValues.bank_account_number,
  });

  // View Invoice in new tab
  const handleViewInvoice = (invoiceType) => {
    const invoiceHTML = generateInvoiceHTML(getInvoiceData(), invoiceType);
    const blob = new Blob([invoiceHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    toast.success(`${invoiceType === 'deposit' ? 'Deposit' : invoiceType === 'receipt' ? 'Deposit Receipt' : 'Balance'} Invoice opened - Use Ctrl+P to save as PDF`);
  };

  // Download Invoice as PDF using backend generation
  const handleDownloadInvoice = async (invoiceType) => {
    const invoiceData = getInvoiceData();
    const invoiceHTML = generateInvoiceHTML(invoiceData, invoiceType);
    const invoiceName = invoiceType === 'deposit' ? 'Deposit' : invoiceType === 'receipt' ? 'Deposit_Receipt' : 'Balance';
    const fileName = `HomeGo_Invoice_${formValues.quotation_reference}_${invoiceName}.pdf`;
    
    // Show loading toast
    const loadingToast = toast.loading(`Generating ${invoiceName} Invoice PDF...`);
    
    try {
      // Use backend to generate PDF
      const response = await axios.post(`${API}/generate-pdf`, {
        html_content: invoiceHTML,
        filename: fileName
      }, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.dismiss(loadingToast);
      toast.success(`${invoiceName} Invoice PDF downloaded!`);
      
    } catch (err) {
      console.error('PDF Error:', err);
      toast.dismiss(loadingToast);
      
      // Fallback: Download as HTML
      const blob = new Blob([invoiceHTML], { type: 'text/html' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `HomeGo_Invoice_${formValues.quotation_reference}_${invoiceName}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      
      toast.info(`Downloaded as HTML - Open & press Ctrl+P to save as PDF`);
    }
  };

  // Attach invoice PDF to the outgoing email
  const handleAttachInvoice = async (invoiceType) => {
    const invoiceData = getInvoiceData();
    const invoiceHTML = generateInvoiceHTML(invoiceData, invoiceType);
    const invoiceName = invoiceType === 'deposit' ? 'Deposit_Invoice' : invoiceType === 'receipt' ? 'Deposit_Receipt' : 'Balance_Invoice';
    const fileName = `HomeGo_${invoiceName}_${formValues.quotation_reference}.pdf`;
    const loadingToast = toast.loading("Generating invoice PDF to attach...");
    try {
      const response = await axios.post(`${API}/generate-pdf`, {
        html_content: invoiceHTML,
        filename: fileName
      }, { responseType: 'blob' });
      const file = new File([response.data], fileName, { type: 'application/pdf' });
      setAttachment(file);
      toast.dismiss(loadingToast);
      toast.success(`Invoice attached! It will be sent with this email.`);
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("Failed to generate invoice PDF");
    }
  };

  const handleReset = () => {
    reset();
    toast.info("Form reset");
  };

  const handleConnectGmail = async (email) => {
    setConnecting(true);
    try {
      const response = await axios.get(`${API}/oauth/gmail/login`, { params: { sender_email: email } });
      // Redirect directly in the same window (works better on mobile)
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.status === 403 ? "Only Head Office can connect Gmail accounts" : "Failed to start connection");
      setConnecting(false);
    }
  };

  const handleAddSenderAccount = async () => {
    if (!newSenderEmail.trim()) { toast.error("Enter the Gmail address"); return; }
    try {
      const { data } = await axios.post(`${API}/sender-accounts`, { email: newSenderEmail.trim(), branch: newSenderBranch });
      setSenderAccounts((prev) => [...prev, data.account]);
      setNewSenderEmail("");
      toast.success(`${data.account.email} added — now connect it to Gmail`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add sender account");
    }
  };

  const handleDeleteSenderAccount = async (account) => {
    if (!window.confirm(`Remove sender ${account.email}? This also disconnects it from Gmail.`)) return;
    try {
      await axios.delete(`${API}/sender-accounts/${account.id}`);
      setSenderAccounts((prev) => prev.filter((a) => a.id !== account.id));
      setConnectedAccounts((prev) => prev.filter((a) => a.email.toLowerCase() !== account.email.toLowerCase()));
      if (senderEmail === account.email && senderAccounts.length > 1) {
        setSenderEmail(senderAccounts.find((a) => a.id !== account.id)?.email || "");
      }
      toast.success("Sender account removed");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove sender account");
    }
  };

  const handleSendEmail = async () => {
    if (!customerEmail) { toast.error("Enter customer email"); return; }
    const isConnected = connectedAccounts.some(acc => acc.email.toLowerCase() === senderEmail.toLowerCase());
    if (!isConnected) { toast.error(`Connect ${senderEmail} first`); return; }

    setSending(true);
    try {
      const subjectMap = {
        quotation: "Your Removals Quote",
        confirmation: "Booking Confirmation",
        deposit: "Deposit Received - Thank You",
        review: "Welcome to Your New Home!",
        affiliate: "Earn £100 For Every Friend You Refer!",
        whychoose: "Why Choose HomeGo Removals",
        partnership: "Partnership Opportunity - HomeGo Removals",
        viewing: formValues.viewing_type === "callback" ? "Your Callback Is Scheduled - HomeGo Removals" : "Your Property Viewing Is Arranged - HomeGo Removals",
        message: formValues.message_subject || "Message from HomeGo Removals"
      };
      const subject = emailType === "message" 
        ? (formValues.message_subject || "Message from HomeGo Removals")
        : `${subjectMap[emailType] || "HomeGo Removals"} - ${formValues.quotation_reference}`;
      
      // Use FormData if there's an attachment
      if (attachment) {
        const formData = new FormData();
        formData.append('to_email', customerEmail);
        formData.append('subject', subject);
        formData.append('html_content', htmlOutput);
        formData.append('sender_email', senderEmail);
        formData.append('attachment', attachment);
        
        const response = await axios.post(`${API}/send-email-with-attachment`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data.success) {
          toast.success(`Sent to ${customerEmail} with attachment!`);
          
          // Track Google Ads conversion
          if (window.trackGoogleAdsConversion) {
            window.trackGoogleAdsConversion();
          }
          
          // Track email in Pro Manager
          await trackEmailInProManager();
          
          setCustomerEmail("");
          setAttachment(null);
          if (fileInputRef.current) fileInputRef.current.value = "";
        }
      } else {
        const response = await axios.post(`${API}/send-email`, {
          to_email: customerEmail,
          subject,
          html_content: htmlOutput,
          sender_email: senderEmail
        });
        if (response.data.success) {
          toast.success(`Sent to ${customerEmail}!`);
          
          // Track Google Ads conversion
          if (window.trackGoogleAdsConversion) {
            window.trackGoogleAdsConversion();
          }
          
          // Track email in Pro Manager
          await trackEmailInProManager();
          
          setCustomerEmail("");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  // Track email sent in Pro Manager CRM
  const trackEmailInProManager = async () => {
    try {
      const trackData = new FormData();
      trackData.append('customer_email', customerEmail);
      trackData.append('customer_name', formValues.customer_name || 'Unknown');
      trackData.append('email_type', emailType === "viewing" && formValues.viewing_type === "callback" ? "callback" : emailType);
      if (authUser?.branch) {
        trackData.append('branch', authUser.branch);
      }
      if (emailType === "viewing") {
        if (formValues.viewing_date) trackData.append('appointment_date', formValues.viewing_date);
        if (formValues.viewing_time) trackData.append('appointment_time', formValues.viewing_time);
      }
      
      // Include customer contact details
      if (formValues.phone_number) {
        trackData.append('phone', formValues.phone_number);
      }
      
      // Include move details
      if (formValues.moving_from) {
        trackData.append('pickup_postcode', formValues.moving_from);
      }
      if (formValues.moving_to) {
        trackData.append('delivery_postcode', formValues.moving_to);
      }
      if (formValues.moving_date) {
        trackData.append('move_date', formValues.moving_date);
      }
      if (formValues.lead_source) {
        trackData.append('lead_source', formValues.lead_source);
      }
      
      // For Quote tab - include ALL package prices
      if (emailType === 'quotation') {
        // Build package prices object
        const packagePrices = {
          van_man: formValues.van_man_price || 285,
          basic: formValues.economy_move_price || 385,
          comfort: formValues.comfort_move_price || 485,
          complete_comfort: formValues.comfort_plus_price || 735,
          premium: formValues.premium_price || 985
        };
        trackData.append('package_prices', JSON.stringify(packagePrices));
        
        // Include selected package (use display name)
        if (formValues.selected_package) {
          trackData.append('package_selected', formValues.selected_package);
        }
        // Use service_total for quote amount
        const quoteAmount = formValues.service_total || formValues.comfort_move_price || 0;
        trackData.append('quote_amount', String(quoteAmount).replace(/[£,]/g, ''));
        
        // Include deposit amount
        if (formValues.deposit_amount) {
          trackData.append('deposit_amount_quoted', String(formValues.deposit_amount));
        }
      }
      
      // For Confirm tab - customer has decided, include package and price
      if (emailType === 'confirmation') {
        // Get the selected package - check multiple possible field names
        const selectedPackage = formValues.selected_package || formValues.deposit_selected_package;
        if (selectedPackage) {
          trackData.append('package_selected', selectedPackage);
        }
        
        // Get the package price - could be in different fields
        const quoteAmount = formValues.service_total || formValues.invoice_move_price || formValues.move_price || formValues.comfort_move_price || 0;
        trackData.append('quote_amount', String(quoteAmount).replace(/[£,]/g, ''));
        
        // Include deposit if set
        if (formValues.deposit_amount) {
          trackData.append('deposit_paid', String(formValues.deposit_amount).replace(/[£,]/g, ''));
        }
        
        // Include all package prices for reference
        const packagePrices = {
          van_man: formValues.van_man_price || 285,
          basic: formValues.economy_move_price || 385,
          comfort: formValues.comfort_move_price || 485,
          complete_comfort: formValues.comfort_plus_price || 735,
          premium: formValues.premium_price || 985
        };
        trackData.append('package_prices', JSON.stringify(packagePrices));
      }
      
      // For Deposit tab - customer has paid deposit and is booked
      if (emailType === 'deposit') {
        // Get selected package from deposit tab fields
        const selectedPackage = formValues.deposit_selected_package || formValues.selected_package;
        if (selectedPackage) {
          trackData.append('package_selected', selectedPackage);
        }
        
        // Get the total price
        const quoteAmount = formValues.deposit_total_amount || formValues.service_total || formValues.invoice_move_price || 0;
        trackData.append('quote_amount', String(quoteAmount).replace(/[£,]/g, ''));
        
        // Get deposit paid
        if (formValues.deposit_amount || formValues.invoice_deposit_amount) {
          const depositPaid = formValues.deposit_amount || formValues.invoice_deposit_amount;
          trackData.append('deposit_paid', String(depositPaid).replace(/[£,]/g, ''));
        }
        
        // Include all package prices for reference
        const packagePrices = {
          van_man: formValues.van_man_price || 285,
          basic: formValues.economy_move_price || 385,
          comfort: formValues.comfort_move_price || 485,
          complete_comfort: formValues.comfort_plus_price || 735,
          premium: formValues.premium_price || 985
        };
        trackData.append('package_prices', JSON.stringify(packagePrices));
      }
      
      // For Review tab - job is completed
      if (emailType === 'review') {
        const quoteAmount = formValues.service_total || formValues.move_price || 0;
        if (quoteAmount) {
          trackData.append('quote_amount', String(quoteAmount).replace(/[£,]/g, ''));
        }
        if (formValues.selected_package) {
          trackData.append('package_selected', formValues.selected_package);
        }
      }
      
      const response = await axios.post(`${API}/pro-manager/track-email`, trackData);
      console.log('Pro Manager tracking response:', response.data);
      
      // Show success notification with link to Pro Manager
      const statusLabels = {
        quotation: 'Pending Quote',
        confirmation: 'Awaiting Decision',
        deposit: 'Job Booked',
        review: 'Job Completed',
        whychoose: 'Quote Sent',
        affiliate: 'Referral Sent',
        partnership: 'Partnership Email Sent',
        message: 'Message Sent'
      };
      
      toast.success(
        <div className="flex flex-col gap-1">
          <span className="font-medium">📊 Tracked in Pro Manager</span>
          <span className="text-xs text-zinc-400">Status: {statusLabels[emailType] || 'Updated'}</span>
          <a 
            href="/pro-manager" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-amber-400 hover:text-amber-300 text-xs underline mt-1"
          >
            Open Pro Manager →
          </a>
        </div>,
        { duration: 5000 }
      );
    } catch (error) {
      console.error('Pro Manager tracking error:', error);
      // Don't show error to user - email was sent successfully
    }
  };

  // Email Scheduling Handler
  const handleScheduleEmail = async () => {
    if (!customerEmail) { toast.error("Enter customer email"); return; }
    if (!scheduleDate) { toast.error("Select a date"); return; }
    if (!scheduleTime) { toast.error("Select a time"); return; }
    
    const isConnected = connectedAccounts.some(acc => acc.email.toLowerCase() === senderEmail.toLowerCase());
    if (!isConnected) { toast.error(`Connect ${senderEmail} first to schedule emails`); return; }

    setScheduling(true);
    try {
      const subjectMap = {
        quotation: "Your Removals Quote",
        confirmation: "Booking Confirmation",
        deposit: "Deposit Received - Thank You",
        review: "Welcome to Your New Home!",
        affiliate: "Earn £100 For Every Friend You Refer!",
        whychoose: "Why Choose HomeGo Removals",
        partnership: "Partnership Opportunity - HomeGo Removals",
        viewing: "Your Property Viewing Is Arranged - HomeGo Removals"
      };
      const subject = `${subjectMap[emailType] || "HomeGo Removals"} - ${formValues.quotation_reference}`;
      
      // Combine date and time to ISO format
      const scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}:00`);
      
      const response = await axios.post(`${API}/scheduled-emails`, {
        to_email: customerEmail,
        subject,
        html_content: htmlOutput,
        sender_email: senderEmail,
        scheduled_time: scheduledDateTime.toISOString(),
        customer_id: selectedCustomer?.id || null,
        email_type: emailType
      });
      
      toast.success(`Email scheduled for ${scheduledDateTime.toLocaleString()}`);
      setShowScheduleModal(false);
      setScheduleDate("");
      setScheduleTime("09:00");
      fetchScheduledEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to schedule email");
    } finally {
      setScheduling(false);
    }
  };

  // Cancel scheduled email
  const handleCancelScheduledEmail = async (emailId) => {
    try {
      await axios.delete(`${API}/scheduled-emails/${emailId}`);
      toast.success("Scheduled email cancelled");
      fetchScheduledEmails();
    } catch (error) {
      toast.error("Failed to cancel email");
    }
  };

  // Send scheduled email now
  const handleSendScheduledNow = async (emailId) => {
    try {
      await axios.post(`${API}/scheduled-emails/${emailId}/send-now`);
      toast.success("Email sent!");
      fetchScheduledEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Limit file size to 10MB
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        e.target.value = "";
        return;
      }
      setAttachment(file);
      toast.success(`File attached: ${file.name}`);
    }
  };

  const handleRemoveAttachment = () => {
    setAttachment(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // PWA Install Handler
  const handleInstallClick = async () => {
    // iOS Safari - show instructions
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    
    if (isIOS && isSafari) {
      toast.info(
        "To install on iOS: Tap the Share button, then 'Add to Home Screen'",
        { duration: 5000 }
      );
      return;
    }
    
    // macOS Safari
    const isMacSafari = /Macintosh/.test(navigator.userAgent) && isSafari;
    if (isMacSafari) {
      toast.info(
        "To install on Mac: Click File > Add to Dock",
        { duration: 5000 }
      );
      return;
    }
    
    // Chrome/Edge - use the install prompt
    if (installPrompt) {
      try {
        await installPrompt.prompt();
        const result = await installPrompt.userChoice;
        if (result.outcome === 'accepted') {
          toast.success("Installing app...");
        }
        setInstallPrompt(null);
      } catch (error) {
        console.error("Install error:", error);
        toast.error("Installation failed. Try again.");
      }
    } else {
      // Fallback message
      toast.info(
        "Use your browser menu to install this app",
        { duration: 4000 }
      );
    }
    setShowInstallBanner(false);
  };

  const dismissInstallBanner = () => {
    setShowInstallBanner(false);
    // Don't show again for this session
    sessionStorage.setItem('installBannerDismissed', 'true');
  };

  const isAccountConnected = (email) => connectedAccounts.some(acc => acc.email.toLowerCase() === email.toLowerCase());

  return (
    <div className="h-screen flex flex-col bg-gray-100" data-testid="email-builder">
      <Toaster position="top-center" richColors />
      
      {/* PWA Install Banner */}
      {showInstallBanner && !isAppInstalled && !sessionStorage.getItem('installBannerDismissed') && (
        <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-gradient-to-r from-green-600 to-green-700 text-white p-4 rounded-xl shadow-2xl z-50 animate-in slide-in-from-bottom duration-300" data-testid="install-banner">
          <button 
            onClick={dismissInstallBanner}
            className="absolute top-2 right-2 text-white/80 hover:text-white"
            aria-label="Dismiss"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-start gap-3">
            <div className="bg-white/20 rounded-lg p-2">
              <Truck className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-sm">Install HomeGo App</h3>
              <p className="text-xs text-white/90 mt-1">
                Get quick access to quotes, confirmations & customer management from your home screen!
              </p>
              <Button 
                onClick={handleInstallClick}
                size="sm"
                className="mt-3 bg-white text-green-700 hover:bg-white/90 h-8 text-xs font-semibold"
                data-testid="install-banner-btn"
              >
                <InstallIcon className="h-3.5 w-3.5 mr-1" />
                Install Now
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Director's Welcome Message (shown once after login) */}
      <DirectorMessage branchName={authUser?.branch_name} />

      {/* Header */}
      <header className="h-14 bg-black px-3 flex items-center justify-between shadow-lg z-20">
        <div className="flex items-center gap-2">
          <Truck className="h-5 w-5 text-[#D4AF37]" />
          <h1 className="text-sm md:text-lg font-bold text-white">
            Home<span className="text-[#D4AF37]">Go</span>
            <span className="hidden sm:inline"> Email Builder</span>
          </h1>
        </div>

        <div className="flex items-center gap-2">
          {/* Desktop View Toggles - Hide on tablet */}
          {!isMobile && !isTablet && (
            <Tabs value={viewMode} onValueChange={setViewMode}>
              <TabsList className="bg-gray-800 border border-gray-700 h-8">
                <TabsTrigger value="desktop" className="h-6 px-2 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                  <Monitor className="h-3.5 w-3.5" />
                </TabsTrigger>
                <TabsTrigger value="tablet" className="h-6 px-2 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                  <Tablet className="h-3.5 w-3.5" />
                </TabsTrigger>
                <TabsTrigger value="mobile" className="h-6 px-2 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                  <Smartphone className="h-3.5 w-3.5" />
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          <Button onClick={handleCopyHTML} size="sm" className="h-8 px-2 md:px-3 bg-[#D4AF37] hover:bg-[#B5952F] text-black">
            {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            <span className="hidden md:inline ml-1">{copied ? "Copied!" : "Copy"}</span>
          </Button>
          <Button onClick={handleDownloadHTML} size="sm" variant="outline" className="h-8 px-2 md:px-3 border-[#D4AF37] text-[#D4AF37]">
            <Download className="h-4 w-4" />
          </Button>
          
          {/* Invoice Download Button - Show for Confirmation and Deposit tabs */}
          {(emailType === 'confirmation' || emailType === 'deposit') && (
            <Button 
              onClick={() => handleDownloadInvoice(emailType === 'deposit' ? 'deposit' : 'balance')} 
              size="sm" 
              className="h-8 px-2 md:px-3 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="download-invoice-btn"
            >
              <FileDown className="h-4 w-4" />
              <span className="hidden md:inline ml-1">{emailType === 'deposit' ? 'Deposit Invoice' : 'Balance Invoice'}</span>
            </Button>
          )}
          
          {/* Install App Button - Only show if not installed */}
          {!isAppInstalled && (
            <Button 
              onClick={handleInstallClick} 
              size="sm" 
              className="h-8 px-2 md:px-3 bg-green-600 hover:bg-green-700 text-white"
              data-testid="install-app-btn"
            >
              <InstallIcon className="h-4 w-4" />
              <span className="hidden md:inline ml-1">Install</span>
            </Button>
          )}
          
          {/* Pro Manager Link */}
          <a 
            href="/pro-manager" 
            target="_blank"
            rel="noopener noreferrer"
            className="h-8 px-2 md:px-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-md flex items-center gap-1 text-sm font-medium border border-zinc-700"
            data-testid="pro-manager-link"
          >
            <BarChart3 className="h-4 w-4 text-amber-500" />
            <span className="hidden md:inline">Pro Manager</span>
          </a>

          {/* Branch Badge + Logout */}
          {authUser && (
            <div className="flex items-center gap-1.5">
              <span className="h-8 px-2 md:px-3 bg-[#D4AF37] text-black rounded-md flex items-center text-xs font-bold" data-testid="branch-badge">
                {authUser.branch_name}
              </span>
              <button
                onClick={logout}
                className="h-8 px-2 bg-zinc-800 hover:bg-red-900 text-white rounded-md flex items-center text-xs font-medium border border-zinc-700"
                data-testid="logout-button"
                title="Sign out"
              >
                <X className="h-3.5 w-3.5" />
                <span className="hidden md:inline ml-1">Logout</span>
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Mobile Tab Bar */}
      {isMobile && (
        <div className="bg-white border-b px-2 py-2 flex gap-2">
          <Button
            onClick={() => setMobileView('edit')}
            variant={mobileView === 'edit' ? "default" : "outline"}
            size="sm"
            className={`flex-1 h-10 ${mobileView === 'edit' ? 'bg-[#D4AF37] text-black hover:bg-[#B5952F]' : ''}`}
          >
            <Edit3 className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button
            onClick={() => setMobileView('preview')}
            variant={mobileView === 'preview' ? "default" : "outline"}
            size="sm"
            className={`flex-1 h-10 ${mobileView === 'preview' ? 'bg-[#D4AF37] text-black hover:bg-[#B5952F]' : ''}`}
          >
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </Button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Edit Form */}
        {(!isMobile || mobileView === 'edit') && (
          <aside className={`${isMobile ? 'w-full' : isTablet ? 'w-[320px]' : 'w-[400px]'} h-full bg-white border-r overflow-hidden`}>
            <ScrollArea className="h-full">
              <div className="p-4 space-y-5">
                {/* Email Type Tabs */}
                <Tabs value={emailType} onValueChange={setEmailType} className="w-full">
                  <TabsList className="w-full grid grid-cols-3 h-auto gap-1 p-1">
                    <TabsTrigger value="quotation" data-testid="quote-tab" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black font-semibold text-xs px-1 py-2 w-full">
                      <FileText className="h-3.5 w-3.5 mr-0.5" />
                      Quote
                    </TabsTrigger>
                    <TabsTrigger value="confirmation" data-testid="confirm-tab" className="data-[state=active]:bg-[#4CAF50] data-[state=active]:text-white font-semibold text-xs px-1 py-2 w-full">
                      <CheckCircle className="h-3.5 w-3.5 mr-0.5" />
                      Confirm
                    </TabsTrigger>
                    <TabsTrigger value="deposit" data-testid="deposit-tab" className="data-[state=active]:bg-[#10B981] data-[state=active]:text-white font-semibold text-xs px-1 py-2 w-full">
                      <Receipt className="h-3.5 w-3.5 mr-0.5" />
                      Deposit
                    </TabsTrigger>
                    <TabsTrigger value="review" data-testid="review-tab" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-white font-semibold text-xs px-1 py-2 w-full">
                      <Star className="h-3.5 w-3.5 mr-0.5" />
                      Review
                    </TabsTrigger>
                    <TabsTrigger value="affiliate" className="data-[state=active]:bg-[#22C55E] data-[state=active]:text-white font-semibold text-xs px-1 py-2 w-full" data-testid="affiliate-tab">
                      <Gift className="h-3.5 w-3.5 mr-0.5" />
                      Refer
                    </TabsTrigger>
                    <TabsTrigger value="whychoose" data-testid="whychoose-tab" className="data-[state=active]:bg-[#8B5CF6] data-[state=active]:text-white font-semibold text-xs px-1 py-2 w-full">
                      <Award className="h-3.5 w-3.5 mr-0.5" />
                      Why Us
                    </TabsTrigger>
                    <TabsTrigger value="partnership" className="data-[state=active]:bg-[#0EA5E9] data-[state=active]:text-white font-semibold text-xs px-1 py-2 w-full" data-testid="partnership-tab">
                      <Building className="h-3.5 w-3.5 mr-0.5" />
                      Partner
                    </TabsTrigger>
                    <TabsTrigger value="message" className="data-[state=active]:bg-[#EC4899] data-[state=active]:text-white font-semibold text-xs px-1 py-2 w-full" data-testid="message-tab">
                      <MessageSquare className="h-3.5 w-3.5 mr-0.5" />
                      Message
                    </TabsTrigger>
                    <TabsTrigger value="viewing" className="data-[state=active]:bg-[#14B8A6] data-[state=active]:text-white font-semibold text-xs px-1 py-2 w-full" data-testid="viewing-tab">
                      <Home className="h-3.5 w-3.5 mr-0.5" />
                      Viewing
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                {/* Customer Database - Luxury Black & Gold */}
                <Card className="border-2 border-[#D4AF37] bg-black overflow-hidden shadow-[0_4px_20px_rgba(212,175,55,0.25)]" data-testid="customer-database-card">
                  <div className="bg-gradient-to-r from-[#D4AF37] via-[#F4D03F] to-[#D4AF37] px-3 py-2.5 flex items-center justify-between">
                    <h3 className="font-bold text-black flex items-center gap-2 text-sm tracking-wide">
                      <Users className="h-4 w-4" />
                      CUSTOMER DATABASE
                    </h3>
                    {authUser && (
                      <span className="text-[10px] font-bold text-black/70 uppercase tracking-widest">{authUser.branch_name}</span>
                    )}
                  </div>
                  <CardContent className="p-3 space-y-2.5 bg-gradient-to-b from-zinc-950 to-black">
                    {/* Search Input */}
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-[#D4AF37]" />
                      <Input
                        placeholder="Search customers..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        className="h-8 pl-7 text-xs bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-[#D4AF37]"
                        data-testid="customer-search-input"
                      />
                    </div>
                    
                    {/* Status Filter */}
                    <div className="flex gap-1 flex-wrap">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className={`h-6 px-2 text-xs border-zinc-700 ${customerStatusFilter === "all" ? "bg-[#D4AF37] text-black border-[#D4AF37] hover:bg-[#c9a532] font-bold" : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800 hover:text-white"}`}
                        onClick={() => setCustomerStatusFilter("all")}
                      >
                        All
                      </Button>
                      {CUSTOMER_STATUSES.map((s) => (
                        <Button 
                          key={s.value}
                          size="sm" 
                          variant="outline"
                          className={`h-6 px-2 text-xs border-zinc-700 ${customerStatusFilter === s.value ? "bg-[#D4AF37] text-black border-[#D4AF37] hover:bg-[#c9a532] font-bold" : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800 hover:text-white"}`}
                          onClick={() => setCustomerStatusFilter(s.value)}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${s.color} mr-1`}></span>
                          {s.label}
                        </Button>
                      ))}
                    </div>
                    
                    {/* Customer count */}
                    <p className="text-[10px] text-[#D4AF37]/80 font-semibold uppercase tracking-wider">{customers.length} customer{customers.length !== 1 ? 's' : ''} found</p>
                    
                    <div className="flex gap-2">
                      <Select value={selectedCustomer?.id || ""} onValueChange={(val) => {
                        const cust = customers.find(c => c.id === val);
                        if (cust) loadCustomer(cust);
                      }}>
                        <SelectTrigger className="h-8 flex-1 bg-zinc-900 border-zinc-700 text-white" data-testid="customer-select">
                          <SelectValue placeholder="Select customer..." />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${CUSTOMER_STATUSES.find(s => s.value === c.status)?.color || 'bg-gray-400'}`}></span>
                                <span className="text-xs font-medium">{c.reference}</span>
                                <span className="text-xs text-gray-600">{c.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="h-8 px-2 bg-zinc-900 border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
                        onClick={() => {
                          setSelectedCustomer(null);
                          setShowCustomerModal(true);
                        }}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {selectedCustomer && (
                      <div className="bg-zinc-900 p-2.5 rounded-lg border border-[#D4AF37]/40">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-bold text-[#D4AF37]">{selectedCustomer.reference}</span>
                          <Select 
                            value={selectedCustomer.status} 
                            onValueChange={(val) => updateCustomerStatus(selectedCustomer.id, val)}
                          >
                            <SelectTrigger className="h-6 w-24 text-xs bg-black border-zinc-700 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CUSTOMER_STATUSES.map((s) => (
                                <SelectItem key={s.value} value={s.value}>
                                  <span className="flex items-center gap-1">
                                    <span className={`w-2 h-2 rounded-full ${s.color}`}></span>
                                    {s.label}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-zinc-300">{selectedCustomer.name}</p>
                          <button
                            onClick={deleteSelectedCustomer}
                            className="text-[10px] text-red-400 hover:text-red-300 font-semibold flex items-center gap-0.5"
                            data-testid="delete-customer-button"
                            title="Delete this customer"
                          >
                            <X className="h-3 w-3" /> Delete
                          </button>
                        </div>
                      </div>
                    )}
                    
                    <Button 
                      onClick={saveCustomer} 
                      disabled={savingCustomer || !formValues.customer_name}
                      className="w-full h-9 bg-gradient-to-r from-[#D4AF37] to-[#B8860B] hover:from-[#c9a532] hover:to-[#a67908] text-black font-bold text-xs tracking-wide shadow-lg"
                      data-testid="save-customer-button"
                    >
                      {savingCustomer ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
                      {selectedCustomer ? "UPDATE CUSTOMER" : "SAVE NEW CUSTOMER"}
                    </Button>
                  </CardContent>
                </Card>

                {/* Message Tab Content - Show at top for message tab */}
                {emailType === "message" && <MessageForm register={register} setValue={setValue} watch={watch} />}


                {/* MULTIPLE LOCATIONS & SPECIAL REQUIREMENTS (Not for Refer/Why Us/Partner/Message tabs) */}
                {emailType !== "affiliate" && emailType !== "whychoose" && emailType !== "partnership" && emailType !== "message" && (
                  <>
                    <Card className="border-2 border-orange-400 bg-orange-50" data-testid="multiple-locations-card">
                  <div className="bg-orange-500 px-3 py-2">
                    <h3 className="font-bold text-white flex items-center gap-2 text-sm">
                      <Truck className="h-4 w-4" />
                      Multiple Locations & Stops
                    </h3>
                  </div>
                  <CardContent className="p-3 space-y-3">
                    {/* Extra Pickup Locations */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs font-semibold text-orange-800">Extra Pickup Locations</Label>
                        <button
                          type="button"
                          onClick={() => setValue("show_extra_pickups", !formValues.show_extra_pickups)}
                          className={`px-3 py-1 text-xs rounded-full ${formValues.show_extra_pickups ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-600'}`}
                        >
                          {formValues.show_extra_pickups ? '✓ Enabled' : '+ Add Pickups'}
                        </button>
                      </div>
                      {formValues.show_extra_pickups && (
                        <div className="space-y-2 bg-white p-2 rounded-lg border border-orange-300">
                          <div>
                            <Label className="text-xs text-orange-700">Pickup 2 (Address/Postcode)</Label>
                            <Input 
                              {...register("pickup_location_1")} 
                              placeholder="e.g., 45 Oak Street, MK12 5AB"
                              className="h-9 mt-1 border-orange-300"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-orange-700">Pickup 3 (Address/Postcode)</Label>
                            <Input 
                              {...register("pickup_location_2")} 
                              placeholder="e.g., 78 High Road, MK13 6CD"
                              className="h-9 mt-1 border-orange-300"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Extra Drop-off Locations */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs font-semibold text-orange-800">Extra Drop-off Locations</Label>
                        <button
                          type="button"
                          onClick={() => setValue("show_extra_dropoffs", !formValues.show_extra_dropoffs)}
                          className={`px-3 py-1 text-xs rounded-full ${formValues.show_extra_dropoffs ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-600'}`}
                        >
                          {formValues.show_extra_dropoffs ? '✓ Enabled' : '+ Add Drop-offs'}
                        </button>
                      </div>
                      {formValues.show_extra_dropoffs && (
                        <div className="space-y-2 bg-white p-2 rounded-lg border border-orange-300">
                          <div>
                            <Label className="text-xs text-orange-700">Drop-off 2 (Address/Postcode)</Label>
                            <Input 
                              {...register("dropoff_location_1")} 
                              placeholder="e.g., 22 Mill Lane, MK15 7EF"
                              className="h-9 mt-1 border-orange-300"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-orange-700">Drop-off 3 (Address/Postcode)</Label>
                            <Input 
                              {...register("dropoff_location_2")} 
                              placeholder="e.g., 99 Park Avenue, MK16 8GH"
                              className="h-9 mt-1 border-orange-300"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Stops Along the Way */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs font-semibold text-orange-800">Stops Along the Route</Label>
                        <button
                          type="button"
                          onClick={() => setValue("show_stops", !formValues.show_stops)}
                          className={`px-3 py-1 text-xs rounded-full ${formValues.show_stops ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-600'}`}
                        >
                          {formValues.show_stops ? '✓ Enabled' : '+ Add Stops'}
                        </button>
                      </div>
                      {formValues.show_stops && (
                        <div className="space-y-2 bg-white p-2 rounded-lg border border-orange-300">
                          <div>
                            <Label className="text-xs text-orange-700">Stop 1 (Address/Postcode)</Label>
                            <Input 
                              {...register("stop_location_1")} 
                              placeholder="e.g., Storage Unit, MK17 9IJ"
                              className="h-9 mt-1 border-orange-300"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-orange-700">Stop 2 (Address/Postcode)</Label>
                            <Input 
                              {...register("stop_location_2")} 
                              placeholder="e.g., Charity Shop, MK18 0KL"
                              className="h-9 mt-1 border-orange-300"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <p className="text-xs text-orange-600 text-center">
                      Enable sections above to add multiple pickup/drop-off locations
                    </p>
                  </CardContent>
                </Card>

                {/* SPECIAL REQUIREMENTS */}
                <Card className="border-2 border-rose-400 bg-rose-50" data-testid="special-requirements-card">
                  <div className="bg-rose-500 px-3 py-2">
                    <h3 className="font-bold text-white flex items-center gap-2 text-sm">
                      <AlertTriangle className="h-4 w-4" />
                      Special Requirements
                    </h3>
                  </div>
                  <CardContent className="p-3">
                    <p className="text-xs text-rose-700 mb-2">Note any special requirements, access issues, or customer requests:</p>
                    <Textarea 
                      {...register("special_requirements")} 
                      placeholder="Example: Customer has mobility issues - need ground floor access only. Piano needs special handling. Parking permit required. Keys to be collected from estate agent first..."
                      className="min-h-[80px] text-sm border-2 border-rose-300 focus:border-rose-500"
                      style={{ fontSize: '14px' }}
                    />
                    <div className="flex items-center gap-2 mt-2">
                      <input 
                        type="checkbox" 
                        {...register("show_special_requirements")}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <Label className="text-xs">Show special requirements in email</Label>
                    </div>
                  </CardContent>
                </Card>
                  </>
                )}


                {/* Scheduled Emails Section */}
                {scheduledEmails.length > 0 && (
                  <Card className="border-2 border-purple-300 bg-purple-50" data-testid="scheduled-emails-card">
                    <div className="bg-purple-600 px-3 py-2">
                      <h3 className="font-bold text-white flex items-center gap-2 text-sm">
                        <CalendarClock className="h-4 w-4" />
                        Scheduled ({scheduledEmails.length})
                      </h3>
                    </div>
                    <CardContent className="p-2 space-y-2 max-h-32 overflow-y-auto">
                      {scheduledEmails.map((email) => (
                        <div key={email.id} className="bg-white p-2 rounded border text-xs flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{email.to_email}</p>
                            <p className="text-gray-500">{new Date(email.scheduled_time).toLocaleString()}</p>
                          </div>
                          <div className="flex gap-1 ml-2">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-6 w-6 p-0 text-green-600 hover:bg-green-50"
                              onClick={() => handleSendScheduledNow(email.id)}
                              title="Send Now"
                            >
                              <Play className="h-3 w-3" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-6 w-6 p-0 text-red-500 hover:bg-red-50"
                              onClick={() => handleCancelScheduledEmail(email.id)}
                              title="Cancel"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                <Separator />

                {/* Reference */}
                <div>
                  <Label className="text-xs font-semibold text-gray-700">Reference Number</Label>
                  <Input {...register("quotation_reference")} className="h-9 mt-1 font-mono" />
                </div>


                {/* Customer Details */}
                <Card className="border-2 border-[#D4AF37]/40">
                  <div className="bg-[#D4AF37] px-3 py-2">
                    <h3 className="font-bold text-black flex items-center gap-2 text-sm">
                      <User className="h-4 w-4" />
                      Customer Details
                    </h3>
                  </div>
                  <CardContent className="p-3 space-y-3">
                    <div>
                      <Label className="text-xs">Customer Name</Label>
                      <Input {...register("customer_name")} className="h-9 mt-1" data-testid="customer-name-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Property Size</Label>
                      <Select value={formValues.property_size} onValueChange={(val) => setValue("property_size", val)}>
                        <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PROPERTY_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Moving Date</Label>
                      <Input {...register("moving_date")} className="h-9 mt-1" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Crew Size</Label>
                        <Select value={formValues.crew_size} onValueChange={(val) => setValue("crew_size", val)}>
                          <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {CREW_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Vehicle</Label>
                        <Select value={formValues.vehicle_type} onValueChange={(val) => {
                          setValue("vehicle_type", val);
                          // Auto-sync package and price when No Van or Man & Van is selected
                          if (val === "No Van (Crew Only)") {
                            setValue("deposit_selected_package", "loading_crew");
                            if (formValues.loading_crew_price) {
                              setValue("deposit_total_amount", formValues.loading_crew_price.toString());
                            }
                          } else if (val === "Man & Van") {
                            setValue("deposit_selected_package", "man_and_van");
                            if (formValues.man_and_van_price) {
                              setValue("deposit_total_amount", formValues.man_and_van_price.toString());
                            }
                          }
                        }}>
                          <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {VEHICLE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Quotation-specific fields */}
                {emailType === "quotation" && <QuotationForm register={register} setValue={setValue} formValues={formValues} customers={customers} />}

                {/* Confirmation-specific fields */}
                {emailType === "confirmation" && <ConfirmationForm register={register} setValue={setValue} formValues={formValues} customers={customers} handleViewInvoice={handleViewInvoice} handleDownloadInvoice={handleDownloadInvoice} handleAttachInvoice={handleAttachInvoice} />}

                {/* Deposit Received fields */}
                {emailType === "deposit" && <DepositForm register={register} setValue={setValue} formValues={formValues} customers={customers} handleViewInvoice={handleViewInvoice} handleDownloadInvoice={handleDownloadInvoice} handleAttachInvoice={handleAttachInvoice} />}

                {/* Review / Job Complete fields */}
                {emailType === "review" && <ReviewForm register={register} formValues={formValues} />}

                {/* Affiliate Marketing fields */}
                {emailType === "affiliate" && <AffiliateForm register={register} formValues={formValues} />}

                {/* Why Choose Us fields */}
                {emailType === "whychoose" && <WhyChooseForm register={register} setValue={setValue} customers={customers} />}

                {/* PARTNERSHIP TAB */}
                {emailType === "viewing" && <ViewingForm register={register} setValue={setValue} formValues={formValues} customerEmail={customerEmail} setCustomerEmail={setCustomerEmail} sending={sending} handleSendEmail={handleSendEmail} crewList={crewList} selectedCrewIds={selectedCrewIds} setSelectedCrewIds={setSelectedCrewIds} newCrewName={newCrewName} setNewCrewName={setNewCrewName} newCrewRole={newCrewRole} setNewCrewRole={setNewCrewRole} addCrewMember={addCrewMember} removeCrewMember={removeCrewMember} saveViewingDraft={saveViewingDraft} resetViewingForm={resetViewingForm} />}

                {emailType === "partnership" && <PartnershipForm register={register} />}

                {/* POSTCODES - Hide for message tab */}
                {emailType !== "message" && (
                <Card className="border-2 border-blue-500 bg-blue-50" data-testid="postcode-card">
                  <div className="bg-blue-600 px-3 py-2">
                    <h3 className="font-bold text-white flex items-center gap-2 text-sm">
                      <Truck className="h-4 w-4" />
                      Move Postcodes
                    </h3>
                  </div>
                  <CardContent className="p-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold text-blue-800">FROM Postcode</Label>
                        <div className="relative">
                          <Input 
                            {...register("moving_from")} 
                            placeholder="e.g. MK11 4BB" 
                            className="h-12 mt-1 border-2 border-blue-400 focus:border-blue-600 font-bold text-lg pr-8 uppercase" 
                            data-testid="moving-from-input"
                            style={{ fontSize: '18px' }}
                          />
                          {formValues.moving_from && (
                            <button
                              type="button"
                              onClick={() => setValue("moving_from", "")}
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 mt-0.5 text-gray-400 hover:text-red-500"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-blue-800">TO Postcode</Label>
                        <div className="relative">
                          <Input 
                            {...register("moving_to")} 
                            placeholder="e.g. MK14 7BB" 
                            className="h-12 mt-1 border-2 border-blue-400 focus:border-blue-600 font-bold text-lg pr-8 uppercase" 
                            data-testid="moving-to-input"
                            style={{ fontSize: '18px' }}
                          />
                          {formValues.moving_to && (
                            <button
                              type="button"
                              onClick={() => setValue("moving_to", "")}
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 mt-0.5 text-gray-400 hover:text-red-500"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-blue-600 mt-2 text-center">Tap field and type your postcode</p>
                  </CardContent>
                </Card>
                )}

                {/* Send Email Card */}
                <Card className="border-2 border-blue-400 bg-blue-50">
                  <div className="bg-blue-600 px-3 py-2">
                    <h3 className="font-bold text-white flex items-center gap-2 text-sm">
                      <Send className="h-4 w-4" />
                      Send Email
                    </h3>
                  </div>
                  <CardContent className="p-3 space-y-3">
                    <div>
                      <Label className="text-xs font-medium text-gray-700">Customer's Email</Label>
                      <Input
                        type="email"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        placeholder="customer@email.com"
                        className="h-9 mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-gray-700">Send From</Label>
                      <Select value={senderEmail} onValueChange={setSenderEmail}>
                        <SelectTrigger className="h-9 mt-1" data-testid="sender-email-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {senderAccounts.map((acc) => (
                            <SelectItem key={acc.id} value={acc.email}>
                              <div className="flex items-center gap-2">
                                {isAccountConnected(acc.email) ? <CheckCircle className="h-3.5 w-3.5 text-green-500" /> : <X className="h-3.5 w-3.5 text-red-400" />}
                                <span className="text-sm">{acc.email}</span>
                                <span className="text-[10px] text-gray-400 uppercase">{SENDER_BRANCH_OPTIONS.find(b => b.value === acc.branch)?.label || acc.branch}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {!isAccountConnected(senderEmail) && (
                      authUser?.role === 'head_office' ? (
                        <Button onClick={() => handleConnectGmail(senderEmail)} disabled={connecting} variant="outline" className="w-full h-9 border-blue-500 text-blue-600" data-testid="connect-gmail-button">
                          {connecting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Link2 className="h-4 w-4 mr-1.5" />}
                          Connect Account
                        </Button>
                      ) : (
                        <p className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="connect-gmail-hint">
                          {senderEmail} isn't connected to Gmail yet — ask Head Office to connect it.
                        </p>
                      )
                    )}
                    {authUser?.role === 'head_office' && (
                      <div className="border border-gray-200 rounded-lg">
                        <button type="button" onClick={() => setShowSenderManager(!showSenderManager)} className="w-full px-3 py-2 text-left text-xs font-medium text-gray-600 flex items-center justify-between" data-testid="manage-senders-toggle">
                          <span>Manage Sender Accounts ({senderAccounts.length})</span>
                          <span>{showSenderManager ? '−' : '+'}</span>
                        </button>
                        {showSenderManager && (
                          <div className="px-3 pb-3 space-y-2">
                            {senderAccounts.map((acc) => (
                              <div key={acc.id} className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-2 py-1.5" data-testid="sender-account-row">
                                <div className="min-w-0">
                                  <p className="text-xs text-gray-800 truncate">{acc.email}</p>
                                  <p className="text-[10px] text-gray-400">{SENDER_BRANCH_OPTIONS.find(b => b.value === acc.branch)?.label || acc.branch} · {isAccountConnected(acc.email) ? 'Connected' : 'Not connected'}</p>
                                </div>
                                <button type="button" onClick={() => handleDeleteSenderAccount(acc)} className="p-1 text-red-400 hover:text-red-600 shrink-0" data-testid="delete-sender-button" title="Remove sender">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ))}
                            <div className="flex gap-1.5 pt-1">
                              <Input value={newSenderEmail} onChange={(e) => setNewSenderEmail(e.target.value)} placeholder="name@homego-removals.com" className="h-8 text-xs flex-1" data-testid="new-sender-email-input" />
                              <select value={newSenderBranch} onChange={(e) => setNewSenderBranch(e.target.value)} className="h-8 text-xs border border-gray-200 rounded-md px-1 bg-white" data-testid="new-sender-branch-select">
                                {SENDER_BRANCH_OPTIONS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                              </select>
                              <Button type="button" onClick={handleAddSenderAccount} className="h-8 px-2 bg-gray-800 text-white text-xs" data-testid="add-sender-button">
                                <Plus className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* File Attachment */}
                    <div>
                      <Label className="text-xs font-medium text-gray-700">Attach File (Optional)</Label>
                      <div className="mt-1">
                        <input
                          ref={fileInputRef}
                          type="file"
                          onChange={handleFileChange}
                          className="hidden"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                        />
                        {!attachment ? (
                          <Button 
                            type="button"
                            variant="outline" 
                            className="w-full h-9 border-dashed border-gray-300 text-gray-600"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <Paperclip className="h-4 w-4 mr-1.5" />
                            Add Attachment
                          </Button>
                        ) : (
                          <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-md">
                            <Paperclip className="h-4 w-4 text-green-600" />
                            <span className="text-xs text-green-700 flex-1 truncate">{attachment.name}</span>
                            <Button 
                              type="button"
                              variant="ghost" 
                              size="sm"
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={handleRemoveAttachment}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                        <p className="text-[10px] text-gray-400 mt-1">PDF, DOC, XLS, Images (Max 10MB)</p>
                      </div>
                    </div>
                    
                    <Button onClick={handleSendEmail} disabled={sending || !customerEmail || !isAccountConnected(senderEmail)} className="w-full h-9 bg-blue-600 hover:bg-blue-700 text-white">
                      {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Mail className="h-4 w-4 mr-1.5" />}
                      {sending ? "Sending..." : `Send ${emailType === "quotation" ? "Quote" : emailType === "confirmation" ? "Confirmation" : emailType === "deposit" ? "Deposit Email" : emailType === "review" ? "Review Request" : "Why Choose Us"}${attachment ? " + File" : ""}`}
                    </Button>
                    
                    {/* Schedule Email Button */}
                    <Button 
                      onClick={() => setShowScheduleModal(true)} 
                      disabled={!customerEmail || !isAccountConnected(senderEmail)} 
                      variant="outline"
                      className="w-full h-9 border-purple-400 text-purple-600 hover:bg-purple-50"
                      data-testid="schedule-email-btn"
                    >
                      <CalendarClock className="h-4 w-4 mr-1.5" />
                      Schedule for Later
                    </Button>
                  </CardContent>
                </Card>

                <Button onClick={handleReset} variant="ghost" className="w-full">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reset Form
                </Button>
              </div>
            </ScrollArea>
          </aside>
        )}

        {/* Preview Pane */}
        {(!isMobile || mobileView === 'preview') && (
          <main className="flex-1 bg-gray-200 overflow-auto p-2 md:p-6">
            <div className="mb-2 text-xs text-gray-500 font-medium">
              <span className={emailType === "quotation" ? "text-[#D4AF37]" : emailType === "confirmation" ? "text-green-600" : emailType === "deposit" ? "text-emerald-600" : "text-purple-600"}>
                {emailType === "quotation" ? "Quotation" : emailType === "confirmation" ? "Confirmation" : emailType === "deposit" ? "Deposit Received" : emailType === "viewing" ? "Property Viewing" : "Why Choose Us"}
              </span>
              {!isMobile && ` • ${viewMode}`}
            </div>
            
            {/* Preview Container with scaling for mobile */}
            {(() => {
              const mobileScaleFactor = isMobile ? Math.min(1, (windowWidth - 16) / 640) : 1;
              const scaledHeight = isMobile ? `${100 / mobileScaleFactor}vh` : 'calc(100vh - 160px)';
              
              return (
                <div 
                  style={{ 
                    width: isMobile ? `${windowWidth - 16}px` : viewMode === 'mobile' ? '375px' : viewMode === 'tablet' ? '768px' : '100%',
                    height: isMobile ? '75vh' : 'auto',
                    margin: '0 auto',
                    overflow: 'hidden',
                  }}
                >
                  <div 
                    className="bg-white rounded-lg shadow-lg"
                    style={{
                      transform: isMobile ? `scale(${mobileScaleFactor})` : 'none',
                      transformOrigin: 'top left',
                      width: isMobile ? '640px' : '100%',
                      height: isMobile ? scaledHeight : 'auto',
                    }}
                  >
                    <div className={`${emailType === "quotation" ? "bg-gray-800" : emailType === "confirmation" ? "bg-green-700" : emailType === "deposit" ? "bg-emerald-600" : "bg-purple-700"} px-3 py-1.5 flex items-center gap-2`}>
                      <div className="flex gap-1">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                        <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                        <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                      </div>
                      <div className="flex-1 ml-2">
                        <div className="bg-gray-700 rounded px-2 py-0.5 text-[11px] text-gray-300 truncate">
                          {emailType === "quotation" ? `Quote - ${formValues.quotation_reference}` : emailType === "confirmation" ? `Confirmation - ${formValues.quotation_reference}` : emailType === "deposit" ? `Deposit Received - ${formValues.quotation_reference}` : `Why Choose Us - ${formValues.quotation_reference}`}
                        </div>
                      </div>
                    </div>
                    <iframe
                      ref={iframeRef}
                      title="Email Preview"
                      className="border-0"
                      style={{ 
                        width: '100%',
                        height: isMobile ? `calc(${scaledHeight} - 30px)` : 'calc(100vh - 160px)', 
                        minHeight: '500px',
                        display: 'block',
                      }}
                    />
                  </div>
                </div>
              );
            })()}
          </main>
        )}
      </div>
      
      {/* Schedule Email Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="schedule-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="bg-purple-600 px-4 py-3 rounded-t-xl flex items-center justify-between">
              <h3 className="font-bold text-white flex items-center gap-2">
                <CalendarClock className="h-5 w-5" />
                Schedule Email
              </h3>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-white hover:bg-purple-700 h-8 w-8 p-0"
                onClick={() => setShowScheduleModal(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-purple-50 p-3 rounded-lg">
                <p className="text-sm text-purple-800">
                  <strong>To:</strong> {customerEmail || "No email entered"}
                </p>
                <p className="text-sm text-purple-700">
                  <strong>Type:</strong> {emailType === "quotation" ? "Quotation" : emailType === "confirmation" ? "Confirmation" : emailType === "deposit" ? "Deposit Received" : emailType === "viewing" ? "Property Viewing" : "Why Choose Us"}
                </p>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Date</Label>
                <Input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="mt-1"
                  data-testid="schedule-date-input"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium">Time</Label>
                <Input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="mt-1"
                  data-testid="schedule-time-input"
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowScheduleModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  onClick={handleScheduleEmail}
                  disabled={scheduling || !scheduleDate || !scheduleTime || !customerEmail}
                  data-testid="confirm-schedule-btn"
                >
                  {scheduling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calendar className="h-4 w-4 mr-2" />}
                  {scheduling ? "Scheduling..." : "Schedule Email"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
